/**
 * Configuration TanStack Query
 * Options par défaut et utilitaires pour toute l'application
 */
import { QueryClient } from '@tanstack/react-query';

/**
 * Configuration optimisée du QueryClient
 * - Cache intelligent avec durées adaptées par type de données
 * - Retry automatique avec backoff exponentiel
 * - Revalidation sur focus/reconnexion
 */
export function createQueryClient() {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Durée avant que les données soient considérées périmées
        staleTime: 5 * 60 * 1000, // 5 minutes par défaut
        
        // Durée de conservation en cache après déréférencement
        gcTime: 30 * 60 * 1000, // 30 minutes (anciennement cacheTime)
        
        // Stratégie de retry
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
        
        // Revalidation automatique
        refetchOnWindowFocus: true,
        refetchOnReconnect: true,
        refetchOnMount: true,
        
        // Ne pas refetch si la fenêtre est en arrière-plan
        refetchInterval: false,
        
        // Comportement réseau
        networkMode: 'online',
      },
      mutations: {
        // Retry pour les mutations (moins agressif)
        retry: 1,
        retryDelay: 1000,
        
        // Mode réseau
        networkMode: 'online',
      },
    },
  });
}

/**
 * Durées de cache recommandées par type de données
 */
export const STALE_TIMES = {
  // Données qui changent rarement
  STATIC: 30 * 60 * 1000, // 30 minutes (plan comptable, exercices fiscaux)
  
  // Données de configuration
  CONFIG: 10 * 60 * 1000, // 10 minutes (organisation, paramètres)
  
  // Données transactionnelles
  TRANSACTIONAL: 2 * 60 * 1000, // 2 minutes (écritures, dashboard)
  
  // Données temps réel
  REALTIME: 30 * 1000, // 30 secondes (notifications, alertes)
  
  // Session utilisateur
  SESSION: 5 * 60 * 1000, // 5 minutes (auth, profil)
} as const;

/**
 * Options de query pour les données statiques (changent rarement)
 */
export const staticQueryOptions = {
  staleTime: STALE_TIMES.STATIC,
  gcTime: 60 * 60 * 1000, // 1 heure
  refetchOnWindowFocus: false,
  refetchOnMount: false,
};

/**
 * Options de query pour les données transactionnelles
 */
export const transactionalQueryOptions = {
  staleTime: STALE_TIMES.TRANSACTIONAL,
  gcTime: 10 * 60 * 1000,
  refetchOnWindowFocus: true,
};

/**
 * Options de query pour les données temps réel
 */
export const realtimeQueryOptions = {
  staleTime: STALE_TIMES.REALTIME,
  gcTime: 5 * 60 * 1000,
  refetchOnWindowFocus: true,
  refetchInterval: 30 * 1000, // Auto-refresh toutes les 30s
};
